import React, { useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import axios from "axios";
import { CONTACT } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-0 border-b-2 border-[#e0d8ca] bg-transparent px-1 py-2.5 text-[16px] text-[#1b2b45] outline-none focus:border-[#c79a5b] transition placeholder:text-[#b8b1a3]";
const labelCls =
  "block text-[12px] font-bold uppercase tracking-[0.14em] text-[#8a92a3] mb-1";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Vyplňte prosím povinná pole", description: "Jméno, e-mail a zpráva jsou povinné." });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      toast({ title: "Poptávka odeslána!", description: "Ozveme se vám co nejdříve. Děkujeme." });
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast({
        title: "Poptávku se nepodařilo odeslat",
        description: "Zkuste to prosím znovu, nebo nám napište přímo na e-mail.",
      });
    } finally {
      setSending(false);
    }
  };

  const items = [
    { icon: Phone, label: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
    { icon: Mail, label: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: CONTACT.city, href: null },
  ];

  return (
    <section id="kontakt" className="py-20 md:py-28 bg-[#f7f4ee]">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="eyebrow-line">Ozvěte se nám</span>
            <h2 className="font-heading mt-5 text-[30px] sm:text-[38px] md:text-[52px] font-bold text-[#1b2b45] leading-tight">
              Pojďme začít
            </h2>
            <p className="mt-5 text-[16px] text-[#6b7385] leading-relaxed max-w-md">
              Máte dotaz nebo chcete nezávaznou cenovou nabídku? Vyplňte
              formulář a my se vám co nejdříve ozveme.
            </p>
          </div>

          <div>
            <form onSubmit={submit} className="space-y-6">
              <div>
                <label className={labelCls}>Jméno *</label>
                <input value={form.name} onChange={set("name")} className={inputCls} placeholder="Vaše jméno" />
              </div>
              <div>
                <label className={labelCls}>Telefon</label>
                <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+420 …" />
              </div>
              <div>
                <label className={labelCls}>E-mail *</label>
                <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="vas@email.cz" />
              </div>
              <div>
                <label className={labelCls}>Zpráva *</label>
                <textarea value={form.message} onChange={set("message")} rows={3} className={`${inputCls} resize-none`} placeholder="Popište nám váš záměr…" />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="font-serif-nav w-full inline-flex items-center justify-center bg-[#1a1a1a] hover:bg-[#000] disabled:opacity-60 text-white text-[15px] font-bold uppercase tracking-[0.1em] px-6 py-4 rounded-none transition-colors"
              >
                {sending ? "Odesílání…" : "Odeslat poptávku"}
              </button>
            </form>

            <div className="mt-10 flex flex-col items-center gap-4">
              {items.map((it) => {
                const Icon = it.icon;
                const inner = (
                  <div className="flex items-center justify-center gap-3">
                    <Icon size={20} className="text-[#c79a5b] shrink-0" />
                    <span className="text-[16px] font-semibold text-[#1b2b45]">{it.label}</span>
                  </div>
                );
                return it.href ? (
                  <a key={it.label} href={it.href} className="block hover:opacity-70 transition-opacity">{inner}</a>
                ) : (
                  <div key={it.label}>{inner}</div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
